import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { LEGACY_REDIRECTS } from './config/routes';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import EnrollmentPage from './pages/EnrollmentPage';
import EnrollmentLandingPage from './pages/EnrollmentLandingPage';
import NewEnrollmentPage from './pages/NewEnrollmentPage';
import CompleteEnrollmentPage from './pages/CompleteEnrollmentPage';
import CoursesPage from './pages/CoursesPage';
import PricingPage from './pages/PricingPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import ContactPage from './pages/ContactPage';
import TeamPage from './pages/TeamPage';
import AboutPage from './pages/AboutPage';
import MethodologyPage from './pages/MethodologyPage';
import PhilosophyPage from './pages/PhilosophyPage';
import CalendarPage from './pages/CalendarPage';
import DashboardPage from './pages/DashboardPage';
import StudentDashboard from './pages/StudentDashboard';
import FAQPage from './pages/FAQPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EnrollmentSuccessPage from './pages/EnrollmentSuccessPage';
import NotFoundPage from './pages/NotFoundPage';
import ImpersonationBanner from './components/ImpersonationBanner';

// Heavy dashboards are code-split (React.lazy) to keep the public bundle small
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const TeacherDashboard = lazy(() => import('./pages/TeacherDashboard'));

// Full-screen loader used as Suspense fallback for lazy routes
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="route-loading">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946]"></div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children, adminOnly = false, teacherOnly = false }) {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (teacherOnly && user.role !== 'teacher' && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Scroll to top/hash on navigation
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const timer = setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
      return () => clearTimeout(timer);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}

// Language sync: set language context from URL prefix
function LanguageSync() {
  const location = useLocation();
  const { toggleLanguage } = useLanguage();
  useEffect(() => {
    if (location.pathname.startsWith('/en')) {
      toggleLanguage('en');
    } else if (location.pathname.startsWith('/es')) {
      toggleLanguage('es');
    }
  }, [location.pathname]);
  return null;
}

// Root redirect: always send `/` to the primary Spanish site (stable SEO target)
function RootRedirect() {
  return <Navigate to="/es/" replace />;
}

// Legacy Squarespace redirect handler
function LegacyRedirect() {
  const location = useLocation();
  const target = LEGACY_REDIRECTS[location.pathname];
  if (target) {
    return <Navigate to={target} replace />;
  }
  return <NotFoundPage />;
}

// Helper: bilingual public routes (renders same component under /es/slug and /en/slug)
function bilingualRoutes(esSlug, enSlug, Component) {
  return [
    <Route key={`es-${esSlug}`} path={`/es${esSlug}`} element={<Component />} />,
    <Route key={`en-${enSlug}`} path={`/en${enSlug}`} element={<Component />} />,
  ];
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Root → primary Spanish site */}
      <Route path="/" element={<RootRedirect />} />

      {/* Bilingual public routes */}
      {bilingualRoutes('/', '/', LandingPage)}
      {bilingualRoutes('/quienes-somos', '/about', AboutPage)}
      {bilingualRoutes('/metodologia', '/methodology', MethodologyPage)}
      {bilingualRoutes('/filosofia', '/philosophy', PhilosophyPage)}
      {bilingualRoutes('/programas', '/programs', CoursesPage)}
      {bilingualRoutes('/calendario', '/calendar', CalendarPage)}
      {bilingualRoutes('/tarifas', '/pricing', PricingPage)}
      {bilingualRoutes('/contacto', '/contact', ContactPage)}
      {bilingualRoutes('/equipo', '/team', TeamPage)}
      {bilingualRoutes('/blog', '/blog', BlogPage)}
      {bilingualRoutes('/preguntas-frecuentes', '/faq', FAQPage)}
      {bilingualRoutes('/privacidad', '/privacy', PrivacyPage)}
      {bilingualRoutes('/terminos', '/terms', TermsPage)}

      {/* Blog post (dynamic slug) */}
      <Route path="/es/blog/:slug" element={<BlogPostPage />} />
      <Route path="/en/blog/:slug" element={<BlogPostPage />} />

      {/* Inscription / Enrollment (bilingual) */}
      {bilingualRoutes('/inscripciones', '/enrollment', EnrollmentLandingPage)}
      {bilingualRoutes('/inscripciones/nueva', '/enrollment/new', NewEnrollmentPage)}
      {bilingualRoutes('/inscripciones/reinscripcion', '/enrollment/reenroll', EnrollmentPage)}
      <Route path="/es/inscripciones/completar/:token" element={<CompleteEnrollmentPage />} />
      <Route path="/en/enrollment/complete/:token" element={<CompleteEnrollmentPage />} />
      <Route path="/enrollment/complete/:token" element={<CompleteEnrollmentPage />} />
      {bilingualRoutes('/inscripciones/exito', '/enrollment/success', EnrollmentSuccessPage)}

      {/* Auth routes (no language prefix — portal) */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected routes (no language prefix — portal) */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/admin/*" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/student" element={<ProtectedRoute><StudentDashboard /></ProtectedRoute>} />
      <Route path="/teacher" element={<ProtectedRoute teacherOnly><TeacherDashboard /></ProtectedRoute>} />

      {/* Legacy Squarespace redirects */}
      <Route path="/inicio" element={<Navigate to="/es/quienes-somos" replace />} />
      <Route path="/acerca-de-nosotros" element={<Navigate to="/es/filosofia" replace />} />
      <Route path="/acerca-de-nosotros-1" element={<Navigate to="/es/quienes-somos" replace />} />
      <Route path="/que-ofrecemos" element={<Navigate to="/es/programas" replace />} />
      <Route path="/calendario-20252026" element={<Navigate to="/es/calendario" replace />} />

      {/* Old paths → new bilingual paths */}
      <Route path="/pricing" element={<Navigate to="/es/tarifas" replace />} />
      <Route path="/contact" element={<Navigate to="/es/contacto" replace />} />
      <Route path="/courses" element={<Navigate to="/es/programas" replace />} />
      <Route path="/about" element={<Navigate to="/es/quienes-somos" replace />} />
      <Route path="/team" element={<Navigate to="/es/equipo" replace />} />
      <Route path="/enrollment" element={<Navigate to="/es/inscripciones" replace />} />
      <Route path="/enrollment/new" element={<Navigate to="/es/inscripciones/nueva" replace />} />
      <Route path="/enrollment/reenroll" element={<Navigate to="/es/inscripciones/reinscripcion" replace />} />
      <Route path="/enrollment/payment" element={<Navigate to="/es/inscripciones/reinscripcion" replace />} />
      <Route path="/blog" element={<Navigate to="/es/blog" replace />} />
      <Route path="/preguntas-frecuentes" element={<Navigate to="/es/preguntas-frecuentes" replace />} />
      <Route path="/privacy" element={<Navigate to="/es/privacidad" replace />} />
      <Route path="/terms" element={<Navigate to="/es/terminos" replace />} />

      {/* Cross-language slug redirects (Spanish slug under /en/ and English slug under /es/) */}
      <Route path="/en/calendario" element={<Navigate to="/en/calendar" replace />} />
      <Route path="/en/quienes-somos" element={<Navigate to="/en/about" replace />} />
      <Route path="/en/metodologia" element={<Navigate to="/en/methodology" replace />} />
      <Route path="/en/filosofia" element={<Navigate to="/en/philosophy" replace />} />
      <Route path="/en/programas" element={<Navigate to="/en/programs" replace />} />
      <Route path="/en/tarifas" element={<Navigate to="/en/pricing" replace />} />
      <Route path="/en/contacto" element={<Navigate to="/en/contact" replace />} />
      <Route path="/en/equipo" element={<Navigate to="/en/team" replace />} />
      <Route path="/en/preguntas-frecuentes" element={<Navigate to="/en/faq" replace />} />
      <Route path="/en/privacidad" element={<Navigate to="/en/privacy" replace />} />
      <Route path="/en/terminos" element={<Navigate to="/en/terms" replace />} />
      <Route path="/en/inscripciones" element={<Navigate to="/en/enrollment" replace />} />
      <Route path="/en/inscripciones/nueva" element={<Navigate to="/en/enrollment/new" replace />} />
      <Route path="/en/inscripciones/reinscripcion" element={<Navigate to="/en/enrollment/reenroll" replace />} />
      <Route path="/es/calendar" element={<Navigate to="/es/calendario" replace />} />
      <Route path="/es/about" element={<Navigate to="/es/quienes-somos" replace />} />
      <Route path="/es/methodology" element={<Navigate to="/es/metodologia" replace />} />
      <Route path="/es/philosophy" element={<Navigate to="/es/filosofia" replace />} />
      <Route path="/es/programs" element={<Navigate to="/es/programas" replace />} />
      <Route path="/es/pricing" element={<Navigate to="/es/tarifas" replace />} />
      <Route path="/es/contact" element={<Navigate to="/es/contacto" replace />} />
      <Route path="/es/team" element={<Navigate to="/es/equipo" replace />} />
      <Route path="/es/enrollment" element={<Navigate to="/es/inscripciones" replace />} />
      <Route path="/es/enrollment/new" element={<Navigate to="/es/inscripciones/nueva" replace />} />
      <Route path="/es/enrollment/reenroll" element={<Navigate to="/es/inscripciones/reinscripcion" replace />} />
      <Route path="/es/faq" element={<Navigate to="/es/preguntas-frecuentes" replace />} />
      <Route path="/es/privacy" element={<Navigate to="/es/privacidad" replace />} />
      <Route path="/es/terms" element={<Navigate to="/es/terminos" replace />} />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <ImpersonationBanner />
          <LanguageSync />
          <ScrollToHash />
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
